import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@/App';
import '@/index.css';

// Get root element and ensure it exists
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Make sure there is a div with id "root" in index.html');
}

// Create and render root with StrictMode
const root = createRoot(rootElement);

// Handle errors during rendering
const handleError = (error: Error) => {
  console.error('[ERROR] Application failed to render:', error);
  
  // Show user-friendly error message
  root.render(
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full space-y-4 text-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Something went wrong
        </h2>
        <p className="text-gray-500">
          The application failed to load. Please try refreshing the page.
        </p>
        {import.meta.env.DEV && (
          <pre className="mt-4 p-4 bg-red-50 text-red-600 text-left text-sm rounded-lg overflow-auto">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );
};

try {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
} catch (error) {
  handleError(error as Error);
}

// Enable hot module replacement in development
if (import.meta.hot) {
  import.meta.hot.accept();
}

// Log environment info
console.log(`[INFO] Running in ${import.meta.env.MODE} mode`);
if (import.meta.env.DEV) {
  console.log('[DEBUG] Hot Module Replacement is enabled');
}

// Log unhandled errors for debugging — do NOT replace the React tree, let ErrorBoundary handle render errors
window.addEventListener('error', (event) => {
  console.error('[ERROR] Unhandled error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('[ERROR] Unhandled promise rejection:', event.reason);
});